import { useState, useCallback } from 'react';
import { useApp } from '../../components/AppProvider';
import { generateQuoteDocument, generateQuoteNumber, generateQuoteDocx, buildClientFileName, downloadBlob } from '../../lib/pdf';
import { Deal } from '../../lib/types';
import { supabase } from '../../lib/supabase';

export type DocumentFormat = 'pdf' | 'docx';

interface UsePDFGenerationResult {
  quoteNumberCalculated: boolean;
  setQuoteNumberCalculated: React.Dispatch<React.SetStateAction<boolean>>;
  generatingPdf: boolean;
  debugMode: boolean;
  setDebugMode: React.Dispatch<React.SetStateAction<boolean>>;
  /** `serviceIds`: solo queste righe dell'offerta nel documento; assente = tutte */
  generateDocument: (deal: Deal, selectedTemplateId: string, services: any[], settings: any, templates: any[], format?: DocumentFormat, serviceIds?: string[]) => Promise<void>;
  /** @deprecated Use generateDocument instead */
  generatePDF: (deal: Deal, selectedTemplateId: string, services: any[], settings: any, templates: any[]) => Promise<void>;
  calculateQuoteNumber: (deal: Deal, settings: any, updateDeal: (id: string, data: any) => Promise<any>) => Promise<void>;
}

export const usePDFGeneration = (): UsePDFGenerationResult => {
  const { toast } = useApp();
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [quoteNumberCalculated, setQuoteNumberCalculated] = useState(false);

  // Function to generate a document (PDF or DOCX)
  const generateDocument = useCallback(async (
    deal: Deal,
    selectedTemplateId: string,
    services: any[],
    settings: any,
    templates: any[],
    format: DocumentFormat = 'pdf',
    serviceIds?: string[]
  ): Promise<void> => {
    if (!deal || !selectedTemplateId || !settings) {
      toast.error("Missing required data for document generation");
      return;
    }

    setGeneratingPdf(true);
    try {
      // Find the selected template
      const template = templates.find(t => t.id === selectedTemplateId);
      if (!template) {
        toast.error('Template not found');
        return;
      }

      // Always fetch fresh deal data to ensure we have the latest created_at and other fields
      let updatedDeal = { ...deal };

      try {
        const { data: freshDealData, error: dealError } = await supabase
          .from('deals')
          .select('*')
          .eq('id', deal.id)
          .single();

        if (dealError) {
          console.error('Error fetching fresh deal data:', dealError);
          // Continue with stale data if fetch fails
        } else if (freshDealData) {
          // Use fresh deal data, but preserve the client object if it exists
          updatedDeal = { ...freshDealData, client: deal.client };
        }
      } catch (err) {
        console.error('Error fetching fresh deal data:', err);
        // Continue with original deal data
      }

      // Always fetch complete client data before proceeding, regardless of current state
      if (deal.client_id) {
        try {
          // If the client property isn't complete or missing sales_rep, fetch it directly
          if (!deal.client || Object.keys(deal.client).length < 3 || !deal.client.sales_rep) {
            // Fetch the client data directly with sales_rep relation
            const { data: clientData } = await supabase
              .from('clients')
              .select(`
                *,
                sales_rep:user_profiles!clients_sales_rep_id_fkey (
                  id,
                  email,
                  full_name,
                  phone
                ),
                assigned_user:user_profiles!clients_assigned_to_fkey (
                  id,
                  email,
                  full_name
                )
              `)
              .eq('id', deal.client_id)
              .single();

            if (clientData) {
              updatedDeal.client = clientData;
            }
          }

          // Fetch referente (persona collegata via client_relationships) per il cliente.
          // Tra più relazioni attive (es. duplicati residui), preferisci quella con email
          // valorizzata: altrimenti il PDF può selezionare il contatto senza email.
          try {
            const { data: relRows } = await supabase
              .from('client_relationships')
              .select('role, area, person:source_client_id ( business_name, email, phone )')
              .eq('relationship_type', 'contatto')
              .eq('is_active', true)
              .eq('target_client_id', deal.client_id)
              .order('is_referente', { ascending: false })
              .order('is_primary_contact', { ascending: false });

            const relRow = (relRows || []).find(r => !!(r as any).person?.email) || (relRows || [])[0];

            if (relRow && (relRow as any).person) {
              const p: any = (relRow as any).person;
              (updatedDeal as any).referente = {
                name: p.business_name, phone: p.phone, email: p.email,
                role: (relRow as any).role, area: (relRow as any).area,
              };
            }
          } catch (err) {
            console.error('Error fetching referente:', err);
          }

          console.log('Deal data for document generation:', { created_at: updatedDeal.created_at, format });
          console.log('Client data for document generation:', updatedDeal.client);

          // Generate document based on format
          if (format === 'docx') {
            await generateQuoteDocx(
              updatedDeal,
              services,
              settings,
              template,
              { serviceIds }
            );
          } else {
            const pdfBlob = await generateQuoteDocument(updatedDeal.id, template.id, 'pdf', { debugMode, serviceIds });
            downloadBlob(pdfBlob, buildClientFileName(updatedDeal, 'pdf'));
          }
        } catch (err) {
          console.error(`Error generating ${format.toUpperCase()}:`, err);
          toast.error(`Error generating ${format.toUpperCase()}. Please try again.`);
          return;
        }
      } else {
        // If there's no client_id, just generate with current data
        if (format === 'docx') {
          await generateQuoteDocx(
            updatedDeal,
            services,
            settings,
            template,
            { serviceIds }
          );
        } else {
          await generateQuoteDocument(updatedDeal.id, template.id, 'pdf', { debugMode, serviceIds });
        }
      }

      const formatLabel = format === 'docx' ? 'Word' : 'PDF';
      toast.success(`${formatLabel} generated successfully`);
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Error generating document: ' + (error instanceof Error ? error.message : ''));
    } finally {
      setGeneratingPdf(false);
    }
  }, [toast, debugMode]);

  // Backward compatibility wrapper
  const generatePDF = useCallback(async (
    deal: Deal,
    selectedTemplateId: string,
    services: any[],
    settings: any,
    templates: any[]
  ): Promise<void> => {
    return generateDocument(deal, selectedTemplateId, services, settings, templates, 'pdf');
  }, [generateDocument]);

  // Function to calculate quote number
  // Nota: useCallback senza dipendenze per evitare ricreazione e loop infiniti
  const calculateQuoteNumber = useCallback(async (
    deal: Deal,
    settings: any,
    updateDeal: (id: string, data: any) => Promise<any>
  ): Promise<void> => {
    if (!deal || !settings) return;

    // Set quoteNumberCalculated immediately to prevent multiple runs
    setQuoteNumberCalculated(true);

    try {
      // Only generate a new quote number if the deal doesn't already have one
      if (!deal.quote_number) {
        console.log('[usePDFGeneration] Deal does not have a quote number, generating one now');
        const quoteNumber = await generateQuoteNumber(deal, settings);

        // Update the deal with the quote number
        try {
          await updateDeal(deal.id, { quote_number: quoteNumber });
          console.log('[usePDFGeneration] Quote number updated in database:', quoteNumber);
        } catch (err) {
          console.warn('[usePDFGeneration] Could not update quote number in database:', err);
        }
      } else {
        console.log('[usePDFGeneration] Deal already has a quote number:', deal.quote_number);
      }
    } catch (err) {
      console.error('[usePDFGeneration] Error handling quote number:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    quoteNumberCalculated,
    setQuoteNumberCalculated,
    generatingPdf,
    debugMode,
    setDebugMode,
    generateDocument,
    generatePDF,
    calculateQuoteNumber
  };
};